/* ---------------------------------------------------------------------------
   RCFS — reinforced concrete flat slab, simple span.

   The simplest complete family in the program, and the one to build first: a
   one-metre strip of slab, singly reinforced, rated on midspan moment under both
   methods. Eleven sheets in the workbook (GENERAL, LAYOUT, DFDLLL, CCLFM, LRLFM,
   POSTLL, POSTLFM, CCASM, LRASM, POSTASM, POSTSUM) reduce to the two capacity
   calculations below plus demand.ts and rating.ts, both of which the other four
   families share.

   A ONE-METRE STRIP. Everything is per metre of slab width: the dead load, the
   wheel lines, the reinforcement area, the moment. `effectiveWidth` is 1000 mm
   and the sheet types it as such rather than deriving it.

   SHEAR IS COMPUTED AND NOT RATED. `DFDLLL` produces VDL = 57.8 kN/m and
   VLL = 80.8 kN/m for the reference bridge, and no sheet in the RCFS workbook
   ever divides them: `LRLFM` and `LRASM` each carry one row, Moment (Midspan).
   That is not an oversight to correct — shear does not govern a flat slab of
   ordinary proportions, and inventing a shear rating CBPLR does not perform
   would be this application asserting an engineering judgement of its own. The
   demands are returned so the report can show them, marked as computed but not
   rated.

   WHAT IS DELIBERATELY NOT DEFAULTED. No material value, no section loss, no
   dimension has a default here. A missing asphalt thickness is a bridge with no
   wearing course; a missing section loss is an undamaged slab. Both are claims,
   and the application must make them explicitly and record where they came
   from. See the header of demand.ts.
   --------------------------------------------------------------------------- */

import { excelRound } from './round.ts';
import { deadLoad, liveLoadDemand, slabDistribution, type DeadLoad, type LiveLoadDemand } from './demand.ts';
import type { RatingLevel, StandardsProfile } from './profile.ts';
import { rate, type Check, type RatingResult } from './rating.ts';
import { allVehicles } from './rating.ts';
import type { VehicleId } from './vehicles.ts';

/* --------------------------- input --------------------------- */

/** A material as the rating actually used it, however it was arrived at. */
export interface MaterialValues {
  /** f'c or fy, MPa. */
  strength: number;
  /** Allowable working stress at each level, MPa. */
  allowable: Readonly<Record<RatingLevel, number>>;
}

export interface RcfsInput {
  /** Clear span, m. `LAYOUT!F33`. */
  span: number;
  /** Slab thickness h, m. `LAYOUT!F32`. */
  slabThickness: number;
  /** Slab width bw, m — the width the dead load is spread over. `LAYOUT!F31`. */
  slabWidth: number;
  /** Carriageway width, m, curb to curb. What the asphalt covers. */
  carriageway: number;
  /** Wearing course thickness, MILLIMETRES. */
  wearingCourse: number;
  /** Weight of one barrier rail, kN/m. */
  barrierRail: number;
  /** kN/m3. */
  concreteUnitWeight: number;
  /** kN/m3. */
  asphaltUnitWeight: number;

  /** The strip being rated, mm. 1000 for a one-metre strip. `CCLFM!H29`. */
  effectiveWidth: number;
  /** Main reinforcement area over that strip, mm2, before section loss. */
  steelArea: number;
  /** Centroid of the main reinforcement above the slab soffit, mm. `CCLFM!J10`. */
  centroidFromBottom: number;
  /**
   * Loss of main reinforcement cross-section to corrosion, per cent.
   *
   * The reference bridge carries 10, which is what makes it a useful test: the
   * damage path is exercised rather than bypassed. Zero is a legitimate value
   * and means "inspected, none found" — which is why it is required rather than
   * defaulted.
   */
  sectionLoss: number;

  concrete: MaterialValues;
  rebar: MaterialValues;
}

/**
 * Area of a number of equal round bars, mm2.
 *
 * Offered because the sheet types the area (`CCLFM!J9` is 6158 for ten 28 mm
 * bars, against 6157.52 computed) and a transcription should be able to show its
 * working. The typed value is what the reference rating used, so this is a
 * convenience for the input form and not something the engine calls.
 */
export function areaOfBars(count: number, diameter: number): number {
  return (count * Math.PI * diameter * diameter) / 4;
}

/* --------------------------- capacity, LFM --------------------------- */

export interface LfmCapacity {
  /** Reinforcement area after section loss, mm2. */
  steelArea: number;
  /** Effective depth to the reinforcement from the compression face, mm. */
  depth: number;
  /**
   * Rectangular stress block depth a, mm, ROUNDED TO A WHOLE MILLIMETRE.
   *
   * `CCLFM!J33` is `ROUND(As*fy/(0.85*f'c*b), 0)` and the rounded value is what
   * the moment is computed from — 105 mm against 104.55. Load-bearing, and
   * recorded as correction C10.
   */
  stressBlock: number;
  /** The same unrounded, so the size of that rounding is visible. */
  stressBlockExact: number;
  /** Ultimate moment capacity, kN-m per strip. `CCLFM!J34`. */
  moment: number;
}

export function lfmCapacity(input: RcfsInput, profile: StandardsProfile): LfmCapacity {
  const steelArea = (1 - input.sectionLoss / 100) * input.steelArea;
  const depth = input.slabThickness * 1000 - input.centroidFromBottom;

  const stressBlockExact =
    (steelArea * input.rebar.strength) /
    (0.85 * input.concrete.strength * input.effectiveWidth);
  const stressBlock = excelRound(stressBlockExact, 0);

  const moment =
    (profile.capacityFactor * steelArea * input.rebar.strength * (depth - stressBlock / 2)) / 1e6;

  return { steelArea, depth, stressBlock, stressBlockExact, moment };
}

/* --------------------------- capacity, ASM --------------------------- */

export interface AsmCapacity {
  /** Reinforcement ratio rho = As/(b.d). */
  ratio: number;
  /** Neutral axis depth factor k. */
  k: number;
  /** Neutral axis depth kd, mm. */
  kd: number;
  /** Lever arm factor j = 1 - k/3. */
  j: number;
  /** Lever arm jd, mm. */
  jd: number;
  /** Capacity set by the concrete's allowable stress, kN-m, per level. */
  concreteMoment: Readonly<Record<RatingLevel, number>>;
  /** Capacity set by the reinforcement's allowable stress, kN-m, per level. */
  rebarMoment: Readonly<Record<RatingLevel, number>>;
  /** The lesser of the two at each level — the capacity that rates. */
  moment: Readonly<Record<RatingLevel, number>>;
  /** Which of the two governed at each level. The report's most useful line. */
  governedBy: Readonly<Record<RatingLevel, 'concrete' | 'reinforcement'>>;
}

/**
 * Working-stress moment capacity, `CCASM!H18:H27`.
 *
 * Straight cracked-section elastic theory: k from the transformed area, then the
 * two candidate capacities — the concrete at its allowable compressive stress
 * and the steel at its allowable tensile stress — and the lesser rates.
 *
 * BOTH LEVELS COME FROM THE SAME TABLE ROW. The workbook looks the inventory
 * stresses up against the year built and types the operating ones as literals
 * (correction C8). Here the caller supplies both, and the application fills both
 * from the same era row or from the same test certificate. Nothing can be half
 * looked up.
 */
export function asmCapacity(input: RcfsInput, profile: StandardsProfile): AsmCapacity {
  const steelArea = (1 - input.sectionLoss / 100) * input.steelArea;
  const depth = input.slabThickness * 1000 - input.centroidFromBottom;

  /* The sheet writes this as As/d/b, in that order. Same value; kept in the
     same order so a reader checking against H16 sees the same expression. */
  const ratio = steelArea / depth / input.effectiveWidth;
  const rhoN = ratio * profile.modularRatio;
  const k = Math.sqrt(2 * rhoN + rhoN * rhoN) - rhoN;
  const kd = k * depth;
  const j = 1 - k / 3;
  const jd = j * depth;

  const concreteMoment = {
    inventory: (0.5 * kd * input.effectiveWidth * input.concrete.allowable.inventory * jd) / 1e6,
    operating: (0.5 * kd * input.effectiveWidth * input.concrete.allowable.operating * jd) / 1e6,
  };
  const rebarMoment = {
    inventory: (steelArea * input.rebar.allowable.inventory * jd) / 1e6,
    operating: (steelArea * input.rebar.allowable.operating * jd) / 1e6,
  };

  const pick = (level: RatingLevel): 'concrete' | 'reinforcement' =>
    concreteMoment[level] < rebarMoment[level] ? 'concrete' : 'reinforcement';

  return {
    ratio,
    k,
    kd,
    j,
    jd,
    concreteMoment,
    rebarMoment,
    moment: {
      inventory: Math.min(concreteMoment.inventory, rebarMoment.inventory),
      operating: Math.min(concreteMoment.operating, rebarMoment.operating),
    },
    governedBy: { inventory: pick('inventory'), operating: pick('operating') },
  };
}

/* --------------------------- the whole rating --------------------------- */

export interface RcfsRating {
  family: 'RCFS';
  continuity: 'simple';
  dead: DeadLoad;
  /** Live-load demand per vehicle, rating truck first. */
  live: readonly LiveLoadDemand[];
  lfm: LfmCapacity;
  asm: AsmCapacity;
  /**
   * Shear demand, computed and not rated.
   *
   * Present so the report can show it and say plainly that CBPLR does not rate
   * it for this family. See the header.
   */
  shearNotRated: { dead: number; live: readonly LiveLoadDemand[] };
  result: RatingResult;
}

/**
 * Rate a simple-span RC flat slab.
 *
 * The whole eleven-sheet workbook, in one call. Reproduces every reference value
 * in `RCFS SIMPLE` — see scripts/cbplr.ts, which asserts them.
 */
export function rateRcfsSimple(input: RcfsInput, profile: StandardsProfile): RcfsRating {
  const dead = deadLoad(
    {
      thickness: input.slabThickness,
      width: input.slabWidth,
      carriageway: input.carriageway,
      wearingCourse: input.wearingCourse,
      barrierRail: input.barrierRail,
      concreteUnitWeight: input.concreteUnitWeight,
      asphaltUnitWeight: input.asphaltUnitWeight,
    },
    input.span,
  );

  const distribution = slabDistribution(input.span);
  const live = allVehicles(profile).map((v) => liveLoadDemand(v.id, input.span, distribution));

  const lfm = lfmCapacity(input, profile);
  const asm = asmCapacity(input, profile);

  const liveMoments: Partial<Record<VehicleId, number>> = {};
  for (const demand of live) liveMoments[demand.vehicle] = demand.moment;

  const checks: Check[] = [
    {
      id: 'moment-midspan',
      label: 'Moment (midspan)',
      method: 'LFM',
      capacity: { inventory: lfm.moment, operating: lfm.moment },
      dead: dead.moment,
      live: liveMoments,
    },
    {
      id: 'moment-midspan',
      label: 'Moment (midspan)',
      method: 'ASM',
      capacity: asm.moment,
      dead: dead.moment,
      live: liveMoments,
      /* Both levels are governed by the same material in every case this
         program can produce — the two allowable stresses scale together — so
         one label is honest. The inventory level is quoted because that is the
         one posting turns on. */
      governedBy: `${asm.governedBy.inventory} allowable stress`,
    },
  ];

  return {
    family: 'RCFS',
    continuity: 'simple',
    dead,
    live,
    lfm,
    asm,
    shearNotRated: { dead: dead.shear, live },
    result: rate(checks, profile),
  };
}
